/**
 * MarkdownText Component
 * Renders markdown text with Ink components and Paean brand styling
 * Supports: bold, italic, code, links, lists, headers, code blocks
 */

import React, { useMemo } from 'react';
import { Text, Box } from 'ink';
import { INK_COLORS } from '../theme/ink-theme.js';

interface MarkdownTextProps {
    children: string;
}

interface TextSegment {
    text: string;
    bold?: boolean;
    italic?: boolean;
    code?: boolean;
    dimColor?: boolean;
}

/**
 * Parse markdown text into styled segments
 */
function parseMarkdown(text: string): TextSegment[] {
    const segments: TextSegment[] = [];

    // Simple regex-based parser for inline formatting
    // Handle: **bold**, *italic*, `code`, ~~strikethrough~~
    const pattern = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(~~[^~]+~~)|(`[^`]+`)/g;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            segments.push({ text: text.slice(lastIndex, match.index) });
        }

        const matched = match[0];

        if (matched.startsWith('**') && matched.endsWith('**')) {
            // Bold
            segments.push({
                text: matched.slice(2, -2),
                bold: true,
            });
        } else if (matched.startsWith('*') && matched.endsWith('*')) {
            // Italic
            segments.push({
                text: matched.slice(1, -1),
                italic: true,
            });
        } else if (matched.startsWith('~~') && matched.endsWith('~~')) {
            // Strikethrough (render as dim)
            segments.push({
                text: matched.slice(2, -2),
                dimColor: true,
            });
        } else if (matched.startsWith('`') && matched.endsWith('`')) {
            // Inline code
            segments.push({
                text: matched.slice(1, -1),
                code: true,
            });
        }

        lastIndex = match.index + matched.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        segments.push({ text: text.slice(lastIndex) });
    }

    return segments;
}

/**
 * Render a single line with markdown formatting
 */
const MarkdownLine: React.FC<{ line: string }> = ({ line }) => {
    const segments = useMemo(() => parseMarkdown(line), [line]);

    return (
        <Text wrap="wrap">
            {segments.map((seg, i) => (
                <Text
                    key={i}
                    bold={seg.bold}
                    italic={seg.italic}
                    dimColor={seg.dimColor}
                    color={seg.code ? INK_COLORS.warning : undefined}
                    backgroundColor={seg.code ? 'gray' : undefined}
                >
                    {seg.text}
                </Text>
            ))}
        </Text>
    );
};

/**
 * MarkdownText Component
 * Renders markdown-formatted text using Ink
 */
export const MarkdownText: React.FC<MarkdownTextProps> = ({ children }) => {
    const lines = useMemo(() => {
        if (!children) return [];
        return children.split('\n');
    }, [children]);

    // Check for code blocks
    const processedContent = useMemo(() => {
        const result: Array<{ type: 'text' | 'code-block'; content: string; lang?: string }> = [];
        let inCodeBlock = false;
        let codeBlockContent = '';
        let codeBlockLang = '';
        let textContent = '';

        for (const line of lines) {
            if (line.startsWith('```')) {
                if (inCodeBlock) {
                    // End code block
                    result.push({
                        type: 'code-block',
                        content: codeBlockContent.trimEnd(),
                        lang: codeBlockLang,
                    });
                    codeBlockContent = '';
                    codeBlockLang = '';
                    inCodeBlock = false;
                } else {
                    // Start code block
                    if (textContent) {
                        result.push({ type: 'text', content: textContent.trimEnd() });
                        textContent = '';
                    }
                    codeBlockLang = line.slice(3).trim();
                    inCodeBlock = true;
                }
            } else if (inCodeBlock) {
                codeBlockContent += line + '\n';
            } else {
                textContent += line + '\n';
            }
        }

        // Handle remaining content
        if (textContent) {
            result.push({ type: 'text', content: textContent.trimEnd() });
        }
        if (inCodeBlock && codeBlockContent) {
            // Unclosed code block
            result.push({
                type: 'code-block',
                content: codeBlockContent.trimEnd(),
                lang: codeBlockLang,
            });
        }

        return result;
    }, [lines]);

    return (
        <Box flexDirection="column">
            {processedContent.map((block, i) => {
                if (block.type === 'code-block') {
                    return (
                        <Box
                            key={i}
                            flexDirection="column"
                            borderStyle="single"
                            borderColor={INK_COLORS.muted}
                            paddingX={1}
                            marginY={1}
                        >
                            {block.lang && (
                                <Text dimColor>{block.lang}</Text>
                            )}
                            <Text color={INK_COLORS.warning}>{block.content}</Text>
                        </Box>
                    );
                }

                // Regular text - process line by line
                return (
                    <Box key={i} flexDirection="column">
                        {block.content.split('\n').map((line, j) => {
                            // Handle headers
                            if (line.startsWith('### ')) {
                                return <Text key={j} bold dimColor>{line.slice(4)}</Text>;
                            }
                            if (line.startsWith('## ')) {
                                return <Text key={j} bold color={INK_COLORS.primary}>{line.slice(3)}</Text>;
                            }
                            if (line.startsWith('# ')) {
                                return <Text key={j} bold underline color={INK_COLORS.primary}>{line.slice(2)}</Text>;
                            }

                            // Handle list items
                            if (line.startsWith('- ') || line.startsWith('* ')) {
                                return (
                                    <Box key={j}>
                                        <Text color={INK_COLORS.muted}>  • </Text>
                                        <MarkdownLine line={line.slice(2)} />
                                    </Box>
                                );
                            }

                            // Handle numbered lists
                            const numberedMatch = line.match(/^(\d+)\.\s/);
                            if (numberedMatch) {
                                return (
                                    <Box key={j}>
                                        <Text color={INK_COLORS.muted}>  {numberedMatch[1]}. </Text>
                                        <MarkdownLine line={line.slice(numberedMatch[0].length)} />
                                    </Box>
                                );
                            }

                            // Regular line
                            return <MarkdownLine key={j} line={line} />;
                        })}
                    </Box>
                );
            })}
        </Box>
    );
};
